// Tiny base64url ↔ ArrayBuffer helpers used during WebAuthn ceremonies.
// The browser's PublicKeyCredentialCreationOptions / RequestOptions expect
// ArrayBuffer for challenge / user.id / allowCredentials[].id, and the
// returned credential exposes ArrayBuffer fields we have to JSON-encode.

export const b64uToBuf = (b64u) => {
    const b64 = b64u.replace(/-/g, "+").replace(/_/g, "/").padEnd(b64u.length + ((4 - (b64u.length % 4)) % 4), "=");
    const bin = atob(b64);
    const buf = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
    return buf.buffer;
};

export const bufToB64u = (buf) => {
    const bytes = new Uint8Array(buf);
    let str = "";
    for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]);
    return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
};

// Hydrate PublicKeyCredentialCreationOptions returned by the server (as JSON
// with base64url string fields) into the structure the browser expects.
export const hydrateCreateOptions = (opts) => {
    const out = { ...opts };
    out.challenge = b64uToBuf(opts.challenge);
    out.user = { ...opts.user, id: b64uToBuf(opts.user.id) };
    if (Array.isArray(opts.excludeCredentials)) {
        out.excludeCredentials = opts.excludeCredentials.map((c) => ({ ...c, id: b64uToBuf(c.id) }));
    }
    return out;
};

export const hydrateRequestOptions = (opts) => {
    const out = { ...opts };
    out.challenge = b64uToBuf(opts.challenge);
    if (Array.isArray(opts.allowCredentials)) {
        out.allowCredentials = opts.allowCredentials.map((c) => ({ ...c, id: b64uToBuf(c.id) }));
    }
    return out;
};

// Serialise a PublicKeyCredential (returned by navigator.credentials.create / .get)
// into the JSON shape py_webauthn's verify_* functions expect.
export const serialiseAttestation = (cred) => ({
    id: cred.id,
    rawId: bufToB64u(cred.rawId),
    type: cred.type,
    response: {
        attestationObject: bufToB64u(cred.response.attestationObject),
        clientDataJSON: bufToB64u(cred.response.clientDataJSON),
        transports: typeof cred.response.getTransports === "function" ? cred.response.getTransports() : [],
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
});

export const serialiseAssertion = (cred) => ({
    id: cred.id,
    rawId: bufToB64u(cred.rawId),
    type: cred.type,
    response: {
        authenticatorData: bufToB64u(cred.response.authenticatorData),
        clientDataJSON: bufToB64u(cred.response.clientDataJSON),
        signature: bufToB64u(cred.response.signature),
        userHandle: cred.response.userHandle ? bufToB64u(cred.response.userHandle) : null,
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
});

export const isWebAuthnSupported = () =>
    typeof window !== "undefined"
    && !!window.PublicKeyCredential
    && typeof navigator.credentials?.create === "function"
    && typeof navigator.credentials?.get === "function";
